/**
 * "WhatsApp me my results and the brochure" — the lead panel under the group
 * affordability calculator's results (/family-pty and /family-pty/calculator).
 *
 * The visitor never retypes anything the calculator already has: the first
 * name is prefilled from Person 1 (editable) and the calculator inputs and
 * results travel with the request. Only the missing bits are asked: WhatsApp
 * number (required) and email (optional). No surname.
 *
 * Posts to the SAME endpoint as InvestmentSummaryForm (same URL building,
 * same attribution) with formContext 'family_pty' + captureMethod
 * 'calculator'; the server makes surname/email optional for that pair only.
 */
import { useState } from 'react';
import { CheckCircle2, Loader2, MessageCircle, Send } from 'lucide-react';
import { captureAttribution } from '../../utils/attribution';
import { CONTACT_INFO } from '../../data/siteContent';
import { trackEvent, trackLeadConversion } from '../../utils/analytics';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE, isPlausiblePhone, toInternationalDigits } from '../../utils/phone';
import { FieldError, inputClass } from './fields';
import { useVoiceConsent, VoiceConsentCheckbox } from '../forms/VoiceConsent';

/** Shape the server's sanitiser accepts (numbers only; max 6 people). */
export interface CalculatorCapturePayload {
  ratePct: number;
  termYears: number;
  people: Array<{
    name: string;
    grossIncome: number;
    livingExpenses: number;
    debtRepayments: number;
    deposit: number;
    maxBond: number;
    maxInstalment: number;
    purchasePrice: number;
  }>;
  totals: { bond: number; instalment: number; purchasePrice: number; bestSinglePurchasePrice: number };
  split: number[];
}

interface CalculatorLeadCaptureProps {
  /** Person 1's typed name ('' when none) — prefills the first-name field. */
  defaultFirstName: string;
  /** Snapshot of the calculator at the moment of submitting. */
  getCalculator: () => CalculatorCapturePayload;
  mode: 'embedded' | 'standalone';
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CalculatorLeadCapture({ defaultFirstName, getCalculator, mode }: CalculatorLeadCaptureProps) {
  // null = untouched → follow Person 1's name as they type it.
  const [nameText, setNameText] = useState<string | null>(null);
  const voice = useVoiceConsent();
  const firstName = nameText ?? defaultFirstName;
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState<{ name?: string; phone?: string; email?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submittedName, setSubmittedName] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = firstName.trim();
    const mail = email.trim().toLowerCase();
    const next: typeof errors = {};
    if (!name) next.name = 'Please enter your first name.';
    if (!isPlausiblePhone(whatsappNumber)) next.phone = 'Please enter a valid WhatsApp number.';
    if (mail && !EMAIL_RE.test(mail)) next.email = 'Please enter a valid email address, or leave it empty.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const calculator = getCalculator();
    setSubmitting(true);
    try {
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      const response = await fetch(
        `https://us-central1-${projectId}.cloudfunctions.net/captureInvestmentSummaryRequest`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...voice.payload,
            firstName: name,
            email: mail,
            whatsappNumber: toInternationalDigits(whatsappNumber, countryCode),
            formContext: 'family_pty',
            captureMethod: 'calculator',
            calculator,
            website: honeypot,
            attribution: captureAttribution(),
          }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }
      setSubmittedName(name);
      trackEvent('family_pty_calculator_lead', { people: calculator.people.length, mode });
      trackLeadConversion('investment_summary_family_pty', { capture_method: 'calculator' });
    } catch (err) {
      setErrors({
        form:
          err instanceof Error && err.message !== 'Submission failed'
            ? err.message
            : `Something went wrong. Please try again, or call us on ${CONTACT_INFO.phone}.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedName !== null) {
    return (
      <div className="bg-white border border-primary/30 rounded-2xl p-4 sm:p-5 flex items-start gap-3" role="status">
        <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-gray-800 min-w-0">
          Thanks {submittedName}. Your results and the Group Millionaire Strategy brochure are on their way to your WhatsApp.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-card min-w-0">
      {/* Honeypot: invisible to people, irresistible to form bots. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="calc-lead-website">Website</label>
        <input id="calc-lead-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>

      <h4 className="font-bold text-gray-900 flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-[#25D366] shrink-0" aria-hidden="true" />
        WhatsApp me my results and the brochure
      </h4>
      <p className="mt-1 text-sm text-gray-500">We'll send these figures and the Group Millionaire Strategy guide to your WhatsApp. Nothing to retype.</p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="min-w-0">
          <label htmlFor="calc-lead-name" className="block text-sm font-medium text-gray-700 mb-1.5">First name</label>
          <input
            id="calc-lead-name"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setNameText(e.target.value)}
            placeholder="Your first name"
            aria-invalid={!!errors.name}
            className={inputClass(!!errors.name)}
          />
          <FieldError message={errors.name} />
        </div>
        <div className="min-w-0">
          <label htmlFor="calc-lead-whatsapp" className="block text-sm font-medium text-gray-700 mb-1.5">WhatsApp number</label>
          <div className="flex gap-2 min-w-0">
            <select
              aria-label="Country code"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="shrink-0 rounded-lg border border-gray-300 px-2 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            <input
              id="calc-lead-whatsapp"
              type="tel"
              autoComplete="tel-national"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              placeholder="82 123 4567"
              aria-invalid={!!errors.phone}
              className={`${inputClass(!!errors.phone)} min-w-0`}
            />
          </div>
          <FieldError message={errors.phone} />
        </div>
        <div className="sm:col-span-2 min-w-0">
          <label htmlFor="calc-lead-email" className="block text-sm font-medium text-gray-700 mb-1.5">Email (optional)</label>
          <input
            id="calc-lead-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            aria-invalid={!!errors.email}
            className={inputClass(!!errors.email)}
          />
          <FieldError message={errors.email} />
        </div>
      </div>

      <FieldError message={errors.form} />

      <VoiceConsentCheckbox consent={voice.consent} ticked={voice.ticked} setTicked={voice.setTicked} className="mb-3" />

      <button
        type="submit"
        disabled={submitting}
        className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-semibold px-8 py-3.5 rounded-full transition-colors shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Sending…
          </>
        ) : (
          <>
            <Send className="w-4 h-4" aria-hidden="true" /> WhatsApp me my results
          </>
        )}
      </button>
    </form>
  );
}
